import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { addCsrfHeader } from '../utils/csrfUtils';
import supabase from '../supabaseClient';
import Step0UserSetup from '../components/Step0UserSetup';
import DietPreferencesStep from '../components/DietPreferencesStep';
import CuisinePreferencesStep from '../components/CuisinePreferencesStep';
import AllergenPreferencesStep from '../components/AllergenPreferencesStep';
import MealPreferencesStep from '../components/MealPreferencesStep';

const UserSetup = () => {
    const { pgUserId, pgUser, sessionToken } = useAuth();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [preferences, setPreferences] = useState({
        diet_ids: [],
        cuisine_ids: [],
        ingredient_ids: [],
        preferred_number_of_breakfast_meals: 3,
        preferred_number_of_lunch_meals: 3,
        preferred_number_of_dinner_meals: 3,
        preferred_breakfast_serving_size: 2,
        preferred_lunch_serving_size: 2,
        preferred_dinner_serving_size: 2
    });

    useEffect(() => {
        if (!pgUserId) {
            navigate('/login');
            return;
        }
        
        // Check if user has already completed setup
        if (pgUser && pgUser.setup_complete) {
            navigate('/');
        }
    }, [pgUserId, pgUser, navigate]);

    const handleStep0Next = () => {
        setCurrentStep(1);
    };

    const handleDietPreferences = (dietIds, goBack = false) => {
        if (goBack) {
            setCurrentStep(0);
            return;
        }
        
        setPreferences(prev => ({
            ...prev,
            diet_ids: dietIds
        }));
        setCurrentStep(2);
    };

    const handleCuisinePreferences = (cuisineIds, goBack = false) => {
        if (goBack) {
            setCurrentStep(1);
            return;
        }
        
        setPreferences(prev => ({
            ...prev,
            cuisine_ids: cuisineIds
        }));
        setCurrentStep(3);
    };

    const handleAllergenPreferences = (ingredientIds, goBack = false) => {
        if (goBack) {
            setCurrentStep(2);
            return;
        }
        
        setPreferences(prev => ({
            ...prev,
            ingredient_ids: ingredientIds
        }));
        setCurrentStep(4);
    };

    const handleMealPreferences = (mealPrefs, goBack = false) => {
        if (goBack) {
            setCurrentStep(3);
            return;
        }
        
        setPreferences(prev => {
            const updatedPreferences = {
                ...prev,
                ...mealPrefs
            };
            
            // This will run after the state is updated
            setTimeout(() => {
                console.log('Updated preferences:', updatedPreferences);
                submitPreferences(updatedPreferences); // Pass the updated preferences directly
            }, 0);
            
            return updatedPreferences;
        });
    };

    const submitPreferences = async (prefsToSubmit) => {
        const prefsToUse = prefsToSubmit || preferences;
        console.log('submitting preferences: ' + JSON.stringify(prefsToUse));
        try {
            // Get the current session for the auth token
            const { data: { session } } = await supabase.auth.getSession();
            // console.log('using session: ' + JSON.stringify(session));
            
            if (!session?.access_token) {
                throw new Error('No valid session found');
            }

            // Add CSRF token to headers along with auth token
            const headers = await addCsrfHeader({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
            });

            // console.log('about to send request to: ' + `${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/users/preferences/${pgUserId}`);

            const response = await fetch(`${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/users/preferences/${pgUserId}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(prefsToUse)
            });

            // console.log('response: ' + JSON.stringify(response));

            
            
            if (response.ok) {
                // Redirect to pricing page instead of meal plan
                navigate('/pricing');
            } else {
                const errorData = await response.json();
                console.error('Failed to save preferences:', errorData);
            }
        } catch (error) {
            console.error('Error saving preferences:', error);
        }
    };

    return (
        <div className="container">
            {currentStep > 0 && (
                <div className="progress-indicator mb-1">
                    <span>Step {currentStep} of 4</span>
                    <div className="progress-dots">
                        {[1, 2, 3, 4].map(step => (
                            <div 
                                key={step} 
                                className={`progress-dot ${currentStep >= step ? 'active' : ''}`}
                            />
                        ))}
                    </div>
                </div>
            )}
            
            {currentStep === 0 && (
                <Step0UserSetup onNext={handleStep0Next} />
            )}
            
            {currentStep === 1 && (
                <DietPreferencesStep 
                    onSave={handleDietPreferences} 
                    initialValues={preferences.diet_ids}
                    sessionToken={sessionToken} 
                />
            )}
            
            {currentStep === 2 && (
                <CuisinePreferencesStep 
                    onSave={handleCuisinePreferences} 
                    initialValues={preferences.cuisine_ids}
                    sessionToken={sessionToken}
                />
            )}
            
            {currentStep === 3 && (
                <AllergenPreferencesStep 
                    onSave={handleAllergenPreferences} 
                    initialValues={preferences.ingredient_ids}
                    sessionToken={sessionToken}
                />
            )}
            
            {currentStep === 4 && (
                <MealPreferencesStep 
                    onSave={handleMealPreferences} 
                    initialValues={{
                        preferred_number_of_breakfast_meals: preferences.preferred_number_of_breakfast_meals,
                        preferred_number_of_lunch_meals: preferences.preferred_number_of_lunch_meals,
                        preferred_number_of_dinner_meals: preferences.preferred_number_of_dinner_meals,
                        preferred_breakfast_serving_size: preferences.preferred_breakfast_serving_size,
                        preferred_lunch_serving_size: preferences.preferred_lunch_serving_size,
                        preferred_dinner_serving_size: preferences.preferred_dinner_serving_size
                    }}
                    sessionToken={sessionToken}
                />
            )}
        </div>
    );
};

export default UserSetup;
